import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../../services/api';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import UsageInfo from '../../components/UsageInfo';

const INP = 'w-full px-4 py-3 rounded-2xl border border-slate-200 text-sm font-bold text-slate-700 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none transition';

function fmt(n) {
  return Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function VergiAyarlari() {
  const [form, setForm] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [mesaj, setMesaj] = useState('');
  const [hata, setHata] = useState('');
  const [ornek, setOrnek] = useState(1100);

  const getir = useCallback(async () => {
    setYukleniyor(true); setHata('');
    try {
      const d = await apiRequest('vergi-ayari');
      setForm(d);
    } catch (e) {
      setHata(e?.message || 'Ayarlar alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => { getir(); }, [getir]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const kaydet = async () => {
    setKaydediliyor(true); setHata(''); setMesaj('');
    try {
      await apiRequest('vergi-ayari', 'PUT', form);
      setMesaj('Vergi ayarları kaydedildi.');
      setTimeout(() => setMesaj(''), 4000);
    } catch (e) {
      setHata(e?.message || 'Kaydedilemedi.');
    } finally {
      setKaydediliyor(false);
    }
  };

  if (yukleniyor || !form) return <LoadingSpinner fullPage text="Vergi ayarları yükleniyor…" />;

  // Canlı önizleme — girilen oranların bir tutara nasıl yansıdığını gösterir
  const kdv = Number(form.konaklamaKdvOrani) || 0;
  const kv = Number(form.konaklamaVergisiOrani) || 0;
  const tutar = Number(ornek) || 0;

  let matrah, kdvTutar, kvTutar, brut;
  if (form.odaFiyatlariVergiDahilMi) {
    matrah = tutar / (1 + kdv / 100 + kv / 100);
    kdvTutar = matrah * (kdv / 100);
    kvTutar = matrah * (kv / 100);
    brut = tutar;
  } else {
    matrah = tutar;
    kdvTutar = tutar * (kdv / 100);
    kvTutar = tutar * (kv / 100);
    brut = tutar + kdvTutar + kvTutar;
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl">
      <PageHeader
        icon="🏛️"
        iconColor="from-violet-600 to-purple-700"
        iconShadow="shadow-violet-200"
        title="Vergi Ayarları"
        subtitle="Oranlar tek yerden yönetilir, fiyatlandırma ekranlarında ayrıca sorulmaz"
      />

      {hata && <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 px-5 py-4 text-sm font-semibold">{hata}</div>}
      {mesaj && <div className="rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-5 py-4 text-sm font-semibold">{mesaj}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ayarlar */}
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-6 space-y-5">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
              Konaklama KDV Oranı (%)
            </label>
            <input type="number" min="0" max="100" step="0.5" className={INP}
              value={form.konaklamaKdvOrani}
              onChange={e => set('konaklamaKdvOrani', parseFloat(e.target.value) || 0)} />
            <p className="text-[11px] text-slate-400 mt-1.5">Konaklama hizmetinde mevzuattaki güncel oran %10'dur.</p>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
              Konaklama Vergisi Oranı (%)
            </label>
            <input type="number" min="0" max="100" step="0.5" className={INP}
              value={form.konaklamaVergisiOrani}
              onChange={e => set('konaklamaVergisiOrani', parseFloat(e.target.value) || 0)} />
            <p className="text-[11px] text-slate-400 mt-1.5">Kanuni oran %2. Aylık beyan edilir, KDV matrahına dahil edilmez.</p>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5 mt-0.5 rounded text-violet-600 border-slate-300"
                checked={form.odaFiyatlariVergiDahilMi}
                onChange={e => set('odaFiyatlariVergiDahilMi', e.target.checked)} />
              <span>
                <span className="block text-sm font-black text-slate-700">Oda fiyatları vergi dahil</span>
                <span className="block text-[11px] text-slate-400 mt-0.5">
                  İşaretliyse fiyatlandırma ekranındaki tutar misafirin ödediği toplamdır, vergiler
                  bu tutarın içinden ayrıştırılır. İşaretli değilse tutar vergisiz taban kabul edilir
                  ve üzerine vergi eklenir.
                </span>
              </span>
            </label>
          </div>

          <button onClick={kaydet} disabled={kaydediliyor}
            className="w-full py-3.5 rounded-2xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-black uppercase tracking-widest disabled:opacity-50 transition">
            {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
          </button>

          {form.guncellemeTarihi && (
            <p className="text-[11px] text-slate-400 text-center">
              Son güncelleme: {new Date(form.guncellemeTarihi).toLocaleString('tr-TR')}
            </p>
          )}
        </div>

        {/* Önizleme */}
        <div className="bg-gradient-to-br from-violet-50 to-white rounded-[2rem] border border-violet-100 shadow-sm p-6 space-y-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-violet-500">Nasıl hesaplanıyor</p>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
              Örnek gecelik fiyat
            </label>
            <input type="number" min="0" step="50" className={INP}
              value={ornek} onChange={e => setOrnek(e.target.value)} />
          </div>

          <div className="bg-white rounded-2xl p-5 space-y-2.5 text-sm">
            <div className="flex justify-between text-slate-500">
              <span>Matrah (vergi hariç)</span>
              <span className="font-bold text-slate-700">{fmt(matrah)} TL</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>KDV %{kdv}</span>
              <span className="font-bold text-slate-700">{fmt(kdvTutar)} TL</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>Konaklama vergisi %{kv}</span>
              <span className="font-bold text-slate-700">{fmt(kvTutar)} TL</span>
            </div>
            <div className="flex justify-between pt-2.5 mt-1 border-t border-slate-100">
              <span className="font-black text-slate-700">Misafirin ödediği</span>
              <span className="font-black text-violet-600">{fmt(brut)} TL</span>
            </div>
          </div>

          <p className="text-[11px] text-slate-500 leading-relaxed">
            {form.odaFiyatlariVergiDahilMi
              ? 'Fiyat listesine yazdığınız tutar misafirin ödediği tutardır; vergiler bu tutarın içindedir. Beyan ve fatura için ayrıştırma otomatik yapılır.'
              : 'Fiyat listesine yazdığınız tutara vergiler eklenir; misafir daha yüksek bir tutar öder.'}
          </p>
        </div>
      </div>

      <UsageInfo
        color="violet"
        description="Vergi oranları mevzuatla belirlenir ve tesise göre değişmez. Bu yüzden her fiyatlandırma ekranında ayrı ayrı sorulmaz, buradan yönetilir."
        scenarios={[
          { title: 'Nerelerde kullanılır?', content: 'Konaklama vergisi raporu, fatura kesimi ve rezervasyon hesap özeti bu oranları okur.' },
          { title: 'Anlaşmalı firmalar istisna', content: 'Firma sözleşmelerinde farklı oran olabildiği için firma kartındaki KDV ve konaklama vergisi alanları ayrı çalışır; buradaki ayar onları etkilemez.' },
          { title: 'Vergi dahil mi hariç mi?', content: 'Tüketiciye gösterilen fiyatın her şey dahil olması beklenir; bu yüzden varsayılan "vergi dahil"dir. Değiştirirseniz misafirden alınan tutar değişir, fiyat listenizi gözden geçirin.' },
          { title: 'Oran değişirse', content: 'Mevzuat değiştiğinde buradan güncelleyin. Geçmiş dönem raporu alırken konaklama vergisi ekranındaki oran alanından o dönemin oranını girebilirsiniz.' },
        ]}
      />
    </div>
  );
}
